import React, { Ref } from 'react';
import {
  Box,
  Container,
  Grid,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ZicassoChat,
  BackNavigationAppBar,
  TemplatePluginHandle,
  ContextMenuItemType,
  ComposerRestoreContent,
} from '@marageti/z4-lib';
import {
  Conversation, Message, GetParticipantsResponse,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import ChooseTemplateDialog from '../../../components/choose-template-dialog';
import MessageSidebar from '../../../components/sidebars/message-sidebar';
import TripRequestAssignToBar from '../../../components/trip-request-assign-to-bar';
import ConversationParticipantNames from '../../../components/conversation-participant-names';
import LogCallModal from '../../../components/modals/log-call-modal';
import LogInternalNoteModal from '../../../components/modals/log-internal-note-modal';
import SendSMSModal from '../../../components/modals/send-sms-modal';

type Props = {
  conversationTitle: string | undefined,
  conversation: Conversation | undefined,
  handleViewClick: (message: Message) => void,
  handleDeleteClick?: (message: Message) => void,
  isError: boolean,
  isLoading: boolean,
  isValidating: boolean,
  isReadOnly: boolean,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  participants: GetParticipantsResponse | undefined,
  onSend: (message: string) => void,
  setShowChooseTemplate: (show: boolean) => void,
  setTemplateText: (text: string) => void,
  showAssignedToToolbar: boolean | undefined,
  showChooseTemplate: boolean,
  templatePluginRef: Ref<TemplatePluginHandle>,
  tripMatchId: string | undefined,
  tripRequestId: string | undefined,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  logCallModalOpen: boolean,
  handleLogCallClose: () => void,
  logInternalNoteModalOpen: boolean,
  handleLogInternalNoteClose: () => void,
  sendSMSModalOpen: boolean,
  handleSendSMSClose: () => void,
  travelerId: string | undefined,
  contextMenuItems: Array<ContextMenuItemType>,
  onUndoSend?: (message: Message) => void | Promise<void>,
  restoreEditorContent?: ComposerRestoreContent,
  onAfterLogCallMessageSent?: () => void | Promise<void>,
};

const ConversationView = ({
  conversationTitle,
  conversation,
  viewer,
  isError,
  isLoading,
  isValidating,
  isReadOnly,
  loadMoreMessages,
  hasMoreMessages,
  messages,
  lastMessageSeenId,
  participants,
  onSend,
  handleViewClick,
  handleDeleteClick,
  setShowChooseTemplate,
  setTemplateText,
  showAssignedToToolbar,
  showChooseTemplate,
  templatePluginRef,
  tripMatchId,
  tripRequestId,
  logCallModalOpen,
  handleLogCallClose,
  logInternalNoteModalOpen,
  handleLogInternalNoteClose,
  sendSMSModalOpen,
  handleSendSMSClose,
  travelerId,
  contextMenuItems,
  onUndoSend = undefined,
  restoreEditorContent = undefined,
  onAfterLogCallMessageSent = undefined,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  return (
    <>
      <BackNavigationAppBar
        primary={conversationTitle || <Skeleton width={75} />}
        secondary={(
          <ConversationParticipantNames
            conversationId={conversation?.id}
            typographyProps={{
              variant: 'caption',
            }}
            skeletonProps={{
              width: 150,
            }}
            enableTooltip
          />
          )}
        defaultPath=".."
      />
      {/* Desktop Assigned To Toolbar */}
      <Box sx={{ mb: 1 }}>
        {!isMobile && showAssignedToToolbar && <TripRequestAssignToBar />}
      </Box>

      <Container
        data-el="conversation-view_container"
        maxWidth="xl"
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          ...(isMobile ? {
            p: 0,
            minHeight: 'calc(100vh - 72px)',
            width: '100% !important',
          } : {
            pt: 3.75,
          }),
        }}
      >
        <Grid
          data-el="conversation-view_grid-container"
          container
          spacing={6}
        >

          {/* Sticky Sidebar */}
          {!isMobile && (
            <Grid
              data-el="conversation-view_sticky-sidebar"
              item
              desktop={4}
              sx={{
                display: 'block',
                borderRight: '1px solid #C2C2C2',
              }}
            >
              {/* Sticky Sidebar */}
              <Box
                sx={{
                  position: 'sticky',
                  pr: 3,
                  height: 'calc(100vh - 96px)',
                  overflow: 'auto',
                }}
              >
                <MessageSidebar
                  variant={tripMatchId ? 'tripMatch' : 'tripRequest'}
                  tripMatchId={tripMatchId}
                  tripRequestId={tripRequestId}
                />
              </Box>
            </Grid>
          )}

          {/* Main Content */}
          <Grid
            data-el="conversation-view_main-content"
            item
            desktop={isMobile ? 12 : 8}
            sx={{
              ...(!isMobile && {
                paddingBottom: '14px',
              }),
              ...(isMobile && {
                flexGrow: 1,
              }),
            }}
          >
            <Box
              data-el="conversation-view_chat-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: '100%',
                maxWidth: '100vw',
                overflow: 'hidden',
                ...(isMobile ? {
                  height: 'calc(100dvh - 72px)',
                  px: '24px',
                } : {
                  height: 'calc(100vh - 86px)',
                }),
                px: {
                  xs: 3,
                  sm: 0,
                },
              }}
            >
              {conversation && (
              <>
                <ZicassoChat
                  viewer={viewer}
                  contextMenuItems={contextMenuItems}
                  onViewClick={handleViewClick}
                  onDeleteClick={handleDeleteClick}
                  insertTemplateProps={{
                    onShowTemplateModal: () => setShowChooseTemplate(true),
                    ref: templatePluginRef,
                  }}
                  isError={isError}
                  isLoading={isLoading}
                  isValidating={isValidating}
                  onLoadPreviousPage={loadMoreMessages}
                  loadPreviousPages={hasMoreMessages}
                  conversation={conversation}
                  messages={messages as Message[] || []}
                  lastMessageSeenId={lastMessageSeenId}
                  onSend={onSend}
                  participants={participants}
                  readOnly={isReadOnly}
                  onUndoSend={onUndoSend}
                  restoreEditorContent={restoreEditorContent}
                />
                <LogCallModal
                  open={logCallModalOpen}
                  onClose={handleLogCallClose}
                  conversationId={conversation.id}
                  onAfterMessageSent={onAfterLogCallMessageSent}
                />
                <LogInternalNoteModal
                  open={logInternalNoteModalOpen}
                  onClose={handleLogInternalNoteClose}
                  conversationId={conversation?.id}
                  subscribeToRealtime={false}
                />
                <SendSMSModal
                  open={sendSMSModalOpen}
                  onClose={handleSendSMSClose}
                  conversationId={conversation?.id}
                  travelerId={travelerId}
                  subscribeToRealtime={false}
                />
              </>
              )}

            </Box>
          </Grid>
        </Grid>
      </Container>
      <ChooseTemplateDialog
        closeDialog={() => setShowChooseTemplate(false)}
        onSubmit={setTemplateText}
        open={showChooseTemplate}
      />
    </>
  );
};

export default ConversationView;
